import React, { useState } from 'react';
import { Plus } from 'react-feather';
import { v4 as uuidv4 } from 'uuid';

import { Button, Modal } from '@/components';
import { SelectInput, SelectOption } from '@/components/select-input/SelectInput';
import { TextInput } from '@/components/text-input';
import { useTranslation } from 'react-i18next';
import { RequestBodyField, useAccountConnect } from '@/hooks/influencer/useAccountConnect';
import { filteredPlatformsList, PlatformsList } from '@/utils/platformsList';
import { DefaultOptionType } from 'antd/es/select';

import styles from './Modals.module.css';

interface SocialField {
  id: string;
  platform: DefaultOptionType | null;
  value: string;
}

interface SocialAccountsModalProps {
  onNext: (user: Api.UserObject) => void;
}

function SocialAccountsModal({ onNext }: SocialAccountsModalProps) {
  const [accounts, setAccounts] = useState<RequestBodyField[]>([]);
  const [fields, setFields] = useState<SocialField[]>([
    { id: uuidv4(), platform: null, value: '' }
  ]);
  const { isLoading, mutate } = useAccountConnect({
    accounts,
    onSuccess: (res) => {
      if (res && (res as Api.UserObject).user_id) {
        onNext(res as Api.UserObject);
      }
    }
  });
  const { t } = useTranslation();

  const handleChange = (index: number, val: DefaultOptionType | null | string) => {
    const newFields = [...fields];
    if (typeof val === 'string') {
      newFields[index].value = val;
    } else {
      newFields[index].platform = val;
    }
    setFields(newFields);
  };

  const handleAddField = () => {
    setFields([...fields, { id: uuidv4(), platform: null, value: '' }]);
  };

  const generateLabel = (type: string): string | undefined => {
    if (type === 'social_network') return t('inviteView.accountModal.username');
    if (type === 'blog') return t('inviteView.accountModal.link');
    return `${t('inviteView.accountModal.username')} / ${t('inviteView.accountModal.link')}`;
  };

  const handleSubmit = () => {
    const filteredData: SocialField[] = fields.filter(
      (p: SocialField) => p.platform !== null && p.value
    );

    const data: RequestBodyField[] = filteredData.map((p: SocialField) => {
      const platform = p.platform as SelectOption;
      const obj: RequestBodyField = {
        platform: platform.value.toString()
      };
      if (platform.type === 'blog') {
        obj.permalink = p.value;
      }
      if (platform.type === 'social_network') {
        obj.handle = `@${p.value}`;
      }
      return obj;
    });
    setAccounts(data);
    mutate();
  };

  const labelRender = (selectedVal: TypeAny) => {
    const selectedPlatform = PlatformsList.find((p) => p.value === selectedVal.value);
    if (selectedPlatform) {
      const { icon: Icon, label } = selectedPlatform;
      return (
        <div className={styles.welcomeModalSelectValue}>
          {selectedPlatform && <Icon />}
          <span>{label}</span>
        </div>
      );
    }
    return undefined;
  };

  return (
    <Modal closeButton={false} modalStyles={{ maxWidth: '480px' }}>
      <div className={styles.welcomeModal}>
        <h2 className={`${styles.welcomeModalTitle} ${styles.BottomMarginSm}`}>
          {t('inviteView.accountModal.title')}
        </h2>
        <div className={styles.welcomeModalDescription}>
          {t('inviteView.accountModal.subtitle')}
        </div>
        <div className={styles.welcomeModalBody}>
          {fields.map((field, index) => (
            <div key={field.id} className={styles.welcomeAccountsModalBodyRow}>
              <SelectInput
                label={t('inviteView.accountModal.platform')}
                options={filteredPlatformsList}
                onChange={(val, objVal) => handleChange(index, objVal)}
                value={field.platform}
                labelRender={labelRender}
              />
              <TextInput
                label={generateLabel(field?.platform?.type)}
                value={field.value}
                onChange={(e) => handleChange(index, e.target.value)}
                placeholder={t('inviteView.accountModal.placeholder')}
                disabled={!field.platform}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddField}
            className={styles.welcomeModalAddButton}
            disabled={!fields[fields.length - 1].value || !fields[fields.length - 1].platform}
          >
            <Plus />
            <span>{t('inviteView.accountModal.addButton')}</span>
          </button>
        </div>
        <div className={styles.welcomeModalButton}>
          <Button isLoading={isLoading} disabled={false} theme="cta" onClick={handleSubmit}>
            {t('inviteView.accountModal.connectButton')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default SocialAccountsModal;
